import { AprType, DepositsOfferProductSubType, LoanOfferProductSubType } from 'embed/constants'
import {
  DepositProductTypesOptions,
  DepositsOfferDetails,
  MortgageLoanType,
  TermUnit,
} from '@evenfinancial/even-ts-static'
import { DeepReadonly } from 'utility-types'

export type CreditPartner = DeepReadonly<{
  imageUrl?: string
  name?: string
  description?: string
  disclaimer?: string
  shouldDisplayPreSelect?: boolean
  subtextOverride?: string
  supportsPersonalizedOffers?: boolean
  supportsPreSelect?: boolean
  uuid?: string
}>

export type CreditOfferEarningProgram = DeepReadonly<{
  cardLevelEarningScheme: CardLevelEarningScheme[]
  personalization?: EarningItemPersonalization
  transactionLevelEarningScheme: TransactionLevelEarningScheme[]
}>

type CardLevelEarningScheme = DeepReadonly<{
  bonus: string
  name: CardLevelEarningName
  personalization?: BonusPersonalization
  redemptionValue: string
  requirement: string
  type: CardLevelEarningType
}>

type BonusPersonalization = DeepReadonly<{
  minimumSpendMet?: boolean
  qualifyingSpend?: string
}>

enum CardLevelEarningType {
  ONE_TIME = 'One Time',
  RECURRING = 'Recurring',
}

enum CardLevelEarningName {
  ANNIVERSARY_BONUS = 'Anniversary Bonus',
  ANNUAL_BONUS = 'Annual Bonus',
  SIGN_UP_BONUS = 'Sign-up Bonus',
  USAGE_BONUS = 'Usage Bonus',
}

export enum Currency {
  USD = 'USD',
}

export type RewardsValueCalculationRequestBody = DeepReadonly<{
  rateTableUuid: string
  aggregates?: RewardsValueAggregate[]
  preferredRedemptionTypes?: RedemptionType[]
}>

export type RewardsValueAggregate = DeepReadonly<{
  amount: number
  uuid: string
}>

export type RewardValueCalculationResponse = DeepReadonly<{
  uuid: string
  rateTableUuid: string
  offerCalculations: OfferCalculation[]
  createdAt: string
  createdBy: string
  spendingProportions?: SpendingProportion[]
  preferredRedemptionTypes?: RedemptionType[]
}>

export type RedemptionType = DeepReadonly<{
  redemptionTypeUuid: string
  weight: number
}>

type SpendingProportion = DeepReadonly<{
  categoryUuid: string
  proportion: number
}>

type OfferCalculation = DeepReadonly<{
  staticOfferUuid: string
  earningProgram: CreditOfferEarningProgram
}>

type TransactionLevelEarningScheme = DeepReadonly<{
  detail?: string
  explaination?: string
  groups: TransactionLevelEarningSchemeGroup[]
  personalization?: EarningItemPersonalization
  type: string
}>

type TransactionLevelEarningSchemeGroup = DeepReadonly<{
  earnRate: number
  earnRateText: string
  items: CategoryEarningItem[] | MerchantEarningItem[]
  personalization: EarningItemPersonalization
}>

type CategoryEarningItem = DeepReadonly<{
  category: EarningProgramCategory
}>

type MerchantEarningItem = DeepReadonly<{
  merchant: Merchant
}>

type EarningProgramCategory = DeepReadonly<{
  displayName?: string
  name?: string
  phrase?: string
  uuid?: string
}>

type Merchant = DeepReadonly<{
  category: EarningProgramCategory
  logo: string
  name: string
  uuid: string
}>

type EarningItemPersonalization = DeepReadonly<{
  totalEarned?: number
  totalSpent?: number
  totalValue?: number
}>

export type CreditOfferDetails = DeepReadonly<{
  additionalDetails?: string[]
  annualIntroFee?: number
  annualIntroFeeTerm?: number
  annualFee?: number
  aprType?: AprType
  cardImageUrl?: string
  cardName?: string
  cardPurposes?: string[]
  cashAdvanceIntroAprTerm?: number
  cashAdvanceIntroAprTermUnits?: string
  details?: string[]
  earningProgram?: CreditOfferEarningProgram
  foreignExchangeFee?: number
  introOfferAmount?: number
  introOfferText?: string
  introOfferType?: string
  maxAnnualFee?: number
  maxBalanceTransferApr?: number
  maxBalanceTransferIntroApr?: number
  maxCashAdvanceApr?: number
  maxCashAdvanceIntroApr?: number
  maximumPenaltyApr?: number
  maxPurchaseApr?: number
  maxPurchaseIntroApr?: number
  minAnnualFee?: number
  minBalanceTransferApr?: number
  minBalanceTransferIntroApr?: number
  minCashAdvanceApr?: number
  minCashAdvanceIntroApr?: number
  minimumPenaltyApr?: number
  minPurchaseApr?: number
  minPurchaseIntroApr?: number
  preApproved?: boolean
  preQualified?: boolean
  preSelected?: boolean
  purchaseAprText?: string
  purchaseIntroAprText?: string
  purchaseIntroAprTerm?: number
  purchaseIntroAprTermUnit?: string
  ratesUrl?: string
  recommendedCreditRatings?: string[]
}>

export type CreditOfferMarketplace = DeepReadonly<{
  description: string
  disclaimer: string
  imageUrl: string
  name: string
  shouldDisplayPreSelect?: boolean
  supportsPersonalizedOffers: boolean
  supportsPreSelect?: boolean
  uuid: string
}>

export type CreditOffer = DeepReadonly<{
  details: CreditOfferDetails
  marketplace?: CreditOfferMarketplace
  partner?: CreditPartner
  productSubType?: string
  productType?: string
  recommendationScore?: number
  uuid: string
  url: string
}>

export type DepositsPartner = DeepReadonly<{
  description?: string
  disclaimer?: string
  imageUrl?: string
  name?: string
  supportsPersonalizedOffers?: boolean
  uuid?: string
}>

export type DepositsOffer = DeepReadonly<{
  details: DepositsOfferDetails
  marketplace?: DepositsPartner
  partner?: DepositsPartner
  productSubType?: DepositsOfferProductSubType | DepositProductTypesOptions
  recommendationScore?: number
  url: string
  uuid: string
}>

export type LifeInsuranceOffer = DeepReadonly<{
  details: LifeInsuranceOfferDetails
  marketplace?: LifeInsurancePartner
  partner: LifeInsurancePartner
  productSubType: LifeInsuranceProductSubType
  productSubTypeDisclaimer?: string
  productType: string
  recommendationScore?: number
  url: string
  uuid: string
  disclaimer?: string
}>

export enum LifeInsuranceProductSubType {
  AccidentalDeathBenefits = 'accidental_death_benefits',
  TermLife = 'term_life',
  TermLifeInstant = 'term_life_instant',
  WholeLife = 'whole_life',
}

export type LifeInsurancePartner = DeepReadonly<{
  amBestRating?: string
  description?: string
  disclaimer?: string
  foundedYear?: number
  imageUrl: string
  name: string
  shouldDisplayPreSelect?: boolean
  supportsPersonalizedOffers?: boolean
  supportsPreSelect?: boolean
  uuid: string
  headquarters?: string
  assets?: string
  subtextOverride?: string
}>

export enum LifeInsuranceMedicalExam {
  NoExamIfQualified = 'no_exam_if_qualified',
  NoExamGuaranteed = 'no_exam_guaranteed',
  ExamRequired = 'exam_required',
}

export const LifeInsuranceMedicalExamLabels: { [key in LifeInsuranceMedicalExam]: string } = {
  [LifeInsuranceMedicalExam.ExamRequired]: 'Medical Exam: Yes',
  [LifeInsuranceMedicalExam.NoExamGuaranteed]: 'No Medical Exam',
  [LifeInsuranceMedicalExam.NoExamIfQualified]: 'Medical Exam: In Some Cases',
}

export const LifeInsuranceADBLabel = 'Accidental Coverage Only'

export type LifeInsuranceOfferDetails = DeepReadonly<{
  policyAmount: number
  monthlyPremiumAmount: number
  annualPremiumAmount?: number
  semiAnnualPremiumAmount?: number
  policyName?: string
  carrierDecisionType?: string
  termLength?: number
  termUnit?: LifeInsuranceOfferTermUnit
  onlineAppAvailable?: boolean
  noMedicalTest?: boolean
  recommended?: boolean
  packageType?: string
  partnerQuoteId?: string
  convertToPermanent?: boolean
  childRider?: string
  deliveryMethod?: string
  examRequired?: string
  medicalExam: LifeInsuranceMedicalExam
}>

enum LifeInsuranceOfferTermUnit {
  DAY = 'day',
  MONTH = 'month',
  YEAR = 'year',
}

export type LoanOriginatorImages = DeepReadonly<{
  sizeKey?: string
  url?: string
}>

export type LoanOriginator = DeepReadonly<{
  name?: string
  description?: string
  disclaimer?: string
  images?: LoanOriginatorImages[]
  key?: string
  bbbRating?: string
  companyUuid: string
}>

export type LoanOffer = DeepReadonly<{
  allowPrepayment?: boolean
  aprType?: AprType
  coApplicant?: boolean
  conversionProbability?: number
  feeFixed?: number
  feeRate?: number
  maxAmount?: number
  maxApr?: number
  maxFeeFixed?: number
  maxFeeRate?: number
  maxMonthlyPayment?: number
  maxTotalPayment?: number
  meanApr?: number
  meanMonthlyPayment?: number
  meanTotalPayment?: number
  minAmount?: number
  minApr?: number
  minFeeFixed?: number
  minFeeRate?: number
  minMonthlyPayment?: number
  minTotalPayment?: number
  monthlyPayment?: number
  originator?: LoanOriginator
  originatorId?: string
  payout?: number
  preApproved: boolean
  preQualified: boolean
  prepaymentFee?: number
  productSubType: LoanOfferProductSubType
  productType?: string
  recommendationScore?: number
  secured?: boolean
  sponsored?: boolean
  termLength: number
  termUnit: TermUnit
  terms?: string
  url: string
  uuid: string
  productSubTypeDisclaimer?: string
  aprDescription?: string
  monthlyPaymentDescription?: string
  termDescription?: string
  amountPrefix?: string
}>

export type MortgageOffer = DeepReadonly<{
  details: MortgageOfferDetails
  disclaimer: string
  partner: MortgageOfferPartner
  url: string
  uuid: string
}>

export type MortgageOfferDetails = DeepReadonly<{
  adjustmentType: string
  apr: number
  interestRate: number
  loanTerm: number
  loanType: MortgageLoanType
  monthlyPayment: number
  netClosingCosts: number
  priceAdjustment: number
}>

export type MortgageOfferPartner = DeepReadonly<{
  description?: string
  disclaimer?: string
  imageUrl: string
  name: string
  uuid: string
}>

export type SpecialOffer = DeepReadonly<{
  uuid: string
  name: string
  desc: string
  url: string
  partnerName: string
  partnerImageUrl: string
  recommendationScore: number
}>

// e.g. the model returned from calling `/originator/staticLoanOffers/:id`
export type OfferDetail = DeepReadonly<{
  createdAt: number
  createdByUuid: string
  creditCard?: CreditOffer
  depositsOffer?: DepositsOffer
  id: number
  loanOffer?: LoanOffer
  mortgageOffer?: MortgageOffer
  name: string
}>

export type Offer = DeepReadonly<
  CreditOffer | DepositsOffer | LifeInsuranceOffer | LoanOffer | MortgageOffer | SpecialOffer
>
